import { type ThunkAction, type ThunkDispatch } from "@reduxjs/toolkit";

import { type Menu, type MenuSection } from "@dashboard/types/menu";
import { type ReduxState } from "@dashboard/types/reduxState";
import { type ThunkExtraArgument } from "@dashboard/types/thunk";

import { type ErrorReducerAction } from "../reducers/errorReducer";
import { type MenuReducerAction } from "../reducers/menuReducer";
import { addError } from "./addErrorAction";

type ThunkActionReturn = ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkExtraArgument,
  MenuReducerAction
>;

export type MenuThunkDispatch = ThunkDispatch<
  ReduxState,
  ThunkExtraArgument,
  MenuReducerAction | ErrorReducerAction
>;

const setMenu: (
  menu: Menu,
  creatingNew: boolean,
  menuSectionsOfMenu?: MenuSection[]
) => ThunkActionReturn = (
  menu: Menu,
  creatingNew: boolean,
  menuSectionsOfMenu?: MenuSection[]
) => {
  return (
    dispatch: MenuThunkDispatch,
    getState: () => ReduxState,
    { menuApi }: ThunkExtraArgument
  ) => {
    // when creating a new menu, we add the new menu to the restaurant's menus
    const previousMenuIdsInDisplayOrder = getState().menu.menuIdsInDisplayOrder;
    return menuApi
      .setMenu(
        menu,
        creatingNew,
        previousMenuIdsInDisplayOrder,
        menuSectionsOfMenu
      )
      .then(() => {
        dispatch({ type: "SET_MENU", payload: { menu, menuSectionsOfMenu } });
      })
      .catch(addError(dispatch));
  };
};

export const toggleMenuStatus: (menu: Menu) => ThunkActionReturn = (
  menu: Menu
) => updateMenu({ ...menu, isViewable: !menu.isViewable } as Menu);

export const updateMenu: (
  menu: Menu,
  menuSectionsOfMenu?: MenuSection[]
) => ThunkActionReturn = (menu: Menu, menuSectionsOfMenu?: MenuSection[]) =>
  setMenu(menu, false, menuSectionsOfMenu);

export const createMenu: (
  menu: Menu,
  menuSectionsOfMenu: MenuSection[]
) => ThunkActionReturn = (menu: Menu, menuSectionsOfMenu?: MenuSection[]) =>
  setMenu(menu, true, menuSectionsOfMenu);

export const setMenuDisplayOrder: (
  menuIdsInDisplayOrder: string[]
) => ThunkActionReturn = (menuIdsInDisplayOrder: string[]) => {
  return (
    dispatch: MenuThunkDispatch,
    getState: () => ReduxState,
    { menuApi }: ThunkExtraArgument
  ) => {
    const restaurantUuid = getState().restaurant.selectedRestaurant!.uuid;
    return menuApi
      .setMenuDisplayOrder(restaurantUuid, menuIdsInDisplayOrder)
      .then(() => {
        dispatch({
          type: "SET_MENU_DISPLAY_ORDER",
          payload: menuIdsInDisplayOrder,
        });
      })
      .catch(addError(dispatch));
  };
};

/**
 * Gets all menus, menu sections, and menuIdsInDisplayOrder for a restaurant
 * to be stored in redux state
 *
 * @param restaurantUuid
 */
export const fetchMenus: () => ThunkActionReturn = () => {
  return (
    dispatch: MenuThunkDispatch,
    getState: () => ReduxState,
    { menuApi }: ThunkExtraArgument
  ) => {
    return menuApi
      .fetchMenus(getState().restaurant.selectedRestaurant!.uuid)
      .then((menus) => {
        dispatch({ type: "FETCHED_MENUS", payload: menus });
      })
      .catch(addError(dispatch));
  };
};

export const deleteMenu: (
  restaurantUuid: string,
  menuId: string
) => ThunkActionReturn = (restaurantUuid: string, menuId: string) => {
  return (
    dispatch: MenuThunkDispatch,
    getState: () => ReduxState,
    { menuApi }: ThunkExtraArgument
  ) => {
    const previousMenuIdsInDisplayOrder = getState().menu.menuIdsInDisplayOrder;
    const menuSectionIdsToDelete = getState().menu.menuSectionsByMenuId[
      menuId
    ].map((menuSection) => menuSection.id);
    return menuApi
      .deleteMenu(
        restaurantUuid,
        menuId,
        previousMenuIdsInDisplayOrder,
        menuSectionIdsToDelete
      )
      .then(() => {
        dispatch({ type: "DELETE_MENU", payload: menuId });
      })
      .catch(addError(dispatch));
  };
};

export const setMenuSection: (menuSection: MenuSection) => ThunkActionReturn = (
  menuSection: MenuSection
) => {
  return (
    dispatch: MenuThunkDispatch,
    getState: () => ReduxState,
    { menuApi }: ThunkExtraArgument
  ) => {
    return menuApi
      .setMenuSection(menuSection)
      .then(() => {
        dispatch({ type: "SET_MENU_SECTION", payload: menuSection });
      })
      .catch(addError(dispatch));
  };
};

export const deleteMenuSection: (
  restaurantUuid: string,
  menuId: string,
  menuSectionId: string
) => ThunkActionReturn = (
  restaurantUuid: string,
  menuId: string,
  menuSectionId: string
) => {
  return (
    dispatch: MenuThunkDispatch,
    getState: () => ReduxState,
    { menuApi }: ThunkExtraArgument
  ) => {
    return menuApi
      .deleteMenuSection(restaurantUuid, menuId, menuSectionId)
      .then(() => {
        dispatch({
          type: "DELETE_MENU_SECTION",
          payload: { menuId, menuSectionId },
        });
      })
      .catch(addError(dispatch));
  };
};
